import { useEffect, useState, useCallback, createContext, useContext } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import API from "@/lib/api";
import AccessGate from "@/components/AccessGate";
import CompanionReferralBanner from "@/components/CompanionReferralBanner";
import CompanionInstallChip from "@/components/CompanionInstallChip";
import CompanionScopeGuard from "@/components/CompanionScopeGuard";
import CompanionStepathonCard from "@/components/CompanionStepathonCard";
import ComingSoonOverlay from "@/components/ComingSoonOverlay";
import useWellnessFeatures from "@/hooks/useWellnessFeatures";
import HealthSyncButton from "@/components/HealthSyncButton";
import ShotRemindersCard from "@/components/ShotRemindersCard";
import CompanionRewardsScreen from "@/pages/CompanionRewardsScreen";
import FitQuizProjection from "@/components/FitQuizProjection";
import MeasurementTrendStrip from "@/components/MeasurementTrendStrip";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import {
  Home, Utensils, Dumbbell, Activity, ArrowLeft, Plus, TrendingUp,
  Flame, Coffee, Beef, Apple, Cookie, ChevronRight, Search, ScanLine, X,
  Syringe, Ruler, ChevronDown, ChevronUp, Gift, Lock, Sparkles, Upload, FileText, Loader2, ExternalLink,
} from "lucide-react";
import { isNativeApp, openExternal } from "@/lib/native";

/**
 * Access context — subscription state shared across all Companion screens.
 * Free tabs (Rewards, Body) work regardless of access. Paid tabs
 * (Food, Move) and paid sub-sections of Home render a lock overlay when
 * `access.has_access === false`.
 */
const AccessContext = createContext({ has_access: true, reason: null });
export const useCompanionAccess = () => useContext(AccessContext);

const BOTTOM_NAV = [
  { id: "home", label: "Home", icon: Home, path: "/companion" },
  { id: "rewards", label: "Rewards", icon: Gift, path: "/companion/rewards" },
  { id: "wellness", label: "Body", icon: Activity, path: "/companion/wellness" },
  { id: "food", label: "Food", icon: Utensils, path: "/companion/food", locked: true },
  { id: "exercise", label: "Move", icon: Dumbbell, path: "/companion/exercise", locked: true },
];

const MEAL_PRESETS = [
  { id: "breakfast", label: "Breakfast", icon: Coffee, color: "bg-amber-100 text-amber-800" },
  { id: "lunch", label: "Lunch", icon: Apple, color: "bg-emerald-100 text-emerald-800" },
  { id: "dinner", label: "Dinner", icon: Beef, color: "bg-rose-100 text-rose-800" },
  { id: "snack", label: "Snack", icon: Cookie, color: "bg-purple-100 text-purple-800" },
];

const EXERCISE_PRESETS = [
  { id: "walking", label: "Walking", emoji: "🚶" },
  { id: "running", label: "Running", emoji: "🏃" },
  { id: "cycling", label: "Cycling", emoji: "🚴" },
  { id: "swimming", label: "Swimming", emoji: "🏊" },
  { id: "yoga", label: "Yoga", emoji: "🧘" },
  { id: "weightlifting", label: "Weights", emoji: "🏋️" },
  { id: "hiit", label: "HIIT", emoji: "💪" },
  { id: "other", label: "Other", emoji: "🤸" },
];

const SIDE_EFFECT_CHIPS = [
  { id: "nausea", label: "Nausea" },
  { id: "fatigue", label: "Fatigue" },
  { id: "gi_upset", label: "GI upset" },
  { id: "headache", label: "Headache" },
  { id: "dizziness", label: "Dizziness" },
  { id: "injection_site_reaction", label: "Inj. site" },
  { id: "decreased_appetite", label: "↓ appetite" },
  { id: "constipation", label: "Constipation" },
  { id: "diarrhea", label: "Diarrhea" },
  { id: "other", label: "Other" },
];

/**
 * Mobile-first PWA companion shell. Bottom-nav into 4 screens:
 *  Home   – Today summary (cals in/out, weight, logs)
 *  Food   – Quick calorie logging w/ meal-type chips
 *  Move   – Quick exercise logging w/ activity presets + auto MET burn
 *  Body   – Weight + side-effect quick-log
 *
 * Reuses /api/wellness/* endpoints — data flows directly into the staff
 * Wellness Tab so prescribers see logs immediately. No separate auth.
 */
export default function CompanionApp() {
  const location = useLocation();
  const tab = location.pathname.split("/")[2] || "home";
  const [access, setAccess] = useState(null);
  const [accessLoaded, setAccessLoaded] = useState(false);
  const wellness = useWellnessFeatures();

  useEffect(() => {
    API.get("/wellness/access")
      .then(({ data }) => setAccess(data))
      .catch(() => setAccess({ has_access: false, reason: "unknown" }))
      .finally(() => setAccessLoaded(true));
  }, []);

  if (!accessLoaded) return <ScreenLoader />;
  // NOTE: We no longer block the entire Companion App on subscription. The
  // Rewards, Body, and Home hub are free-tier — patients on one-time-order
  // (or with no active sub) can still use them. Food + Move tabs and paid
  // sub-sections of Home render a lock overlay via `useCompanionAccess()`.
  return (
    <AccessContext.Provider value={access || { has_access: false, reason: "unknown" }}>
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 pb-20" data-testid="companion-app">
      <CompanionScopeGuard />
      {/* Page content — narrow phone-column on mobile, comfortable desktop width on md+ */}
      <div className="max-w-md md:max-w-2xl lg:max-w-3xl mx-auto md:px-6 lg:px-8">
        {tab === "home" && <HomeScreen />}
        {tab === "rewards" && (
          wellness.rewards_enabled ? (
            <CompanionRewardsScreen />
          ) : (
            <ComingSoonOverlay
              message="Coming soon"
              detail={wellness.coming_soon_message}
              className="min-h-[60vh]"
              testid="coming-soon-rewards"
            >
              <CompanionRewardsScreen />
            </ComingSoonOverlay>
          )
        )}
        {tab === "food" && <FoodScreen />}
        {tab === "exercise" && <ExerciseScreen />}
        {tab === "wellness" && <BodyScreen />}
      </div>

      {/* Bottom nav */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-800 z-50" data-testid="companion-bottom-nav">
        <div className="max-w-md md:max-w-2xl lg:max-w-3xl mx-auto grid grid-cols-5">
          {BOTTOM_NAV.map((item) => {
            const Icon = item.icon;
            const active = tab === item.id;
            const showLock = item.locked && access && !access.has_access;
            const soon = item.id === "rewards" && !wellness.rewards_enabled;
            return (
              <Link
                key={item.id}
                to={item.path}
                className={`relative flex flex-col items-center justify-center py-2.5 ${active ? "text-purple-600" : soon ? "text-slate-400 dark:text-slate-500" : "text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200"}`}
                data-testid={`companion-nav-${item.id}`}
              >
                <Icon className={`w-5 h-5 ${active ? "" : "opacity-70"} ${soon ? "opacity-50" : ""}`} />
                <span className={`text-[10px] mt-0.5 font-medium ${active ? "font-bold" : ""} ${soon ? "line-through" : ""}`}>{item.label}</span>
                {soon && (
                  <span className="absolute top-0.5 right-2 text-[8px] font-bold text-slate-400 uppercase tracking-wider">Soon</span>
                )}
                {showLock && !soon && (
                  <Lock className="absolute top-1 right-1/4 w-2.5 h-2.5 text-slate-400" />
                )}
              </Link>
            );
          })}
        </div>
      </nav>
    </div>
    </AccessContext.Provider>
  );
}

// ===== HOME =====
function HomeScreen() {
  const access = useCompanionAccess();
  const hasSub = !!access?.has_access;
  const wellness = useWellnessFeatures();
  const [data, setData] = useState(null);
  const [macros, setMacros] = useState(null);
  const [profile, setProfile] = useState(null);
  const [fitQuiz, setFitQuiz] = useState(null);
  const [actualWeights, setActualWeights] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const load = useCallback(() => {
    setLoading(true);
    // Non-subscribers skip the macros fetch (backend returns 403) — the UI
    // shows a lock card instead. `/wellness/today` still works for anyone
    // to render an empty "no logs today" state cleanly.
    const macroReq = hasSub
      ? API.get("/wellness/macros/today").catch(() => ({ data: null }))
      : Promise.resolve({ data: null });
    Promise.all([
      API.get("/wellness/today").catch(() => ({ data: { net_calories: 0, calories_in: 0, calories_out: 0, calorie_logs: [], exercise_logs: [] } })),
      macroReq,
      API.get("/wellness/profile").catch(() => ({ data: null })),
      API.get("/wellness/fit-quiz/me").catch(() => ({ data: { submission: null } })),
      API.get("/wellness/weight/series?limit=14").catch(() => ({ data: { series: [] } })),
    ])
      .then(([t, m, p, fq, ws]) => {
        setData(t.data);
        setMacros(m.data);
        setProfile(p.data);
        setFitQuiz(fq.data?.submission || null);
        setActualWeights(Array.isArray(ws.data?.series) ? ws.data.series : []);
      })
      .catch(() => toast.error("Couldn't load today's summary"))
      .finally(() => setLoading(false));
  }, [hasSub]);
  useEffect(() => { load(); }, [load]);
  if (loading) return <ScreenLoader />;
  const showCombined = profile?.pair_weighin_injection && profile?.is_weigh_in_day && !profile?.already_logged_today;
  return (
    <div className="p-4 space-y-4" data-testid="companion-home">
      <header className="flex items-center justify-between">
        <div>
          <p className="text-xs text-slate-500 uppercase tracking-wider font-bold">{new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}</p>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>Today</h1>
        </div>
        {isNativeApp() ? (
          <button
            type="button"
            onClick={() => openExternal("/dashboard")}
            className="text-xs text-slate-400 hover:text-slate-700 inline-flex items-center gap-0.5"
            data-testid="companion-open-full-site"
          >
            Full website <ExternalLink className="w-3 h-3 inline" />
          </button>
        ) : (
          <Link to="/dashboard" className="text-xs text-slate-400 hover:text-slate-700" data-testid="companion-full-dashboard-link">Full dashboard <ChevronRight className="w-3 h-3 inline" /></Link>
        )}
      </header>

      {/* Combined weekly check-in card — only on weigh-in day when pair-toggle is ON */}
      {showCombined && (
        <button
          type="button"
          onClick={() => navigate("/companion/wellness", { state: { mode: "combined" } })}
          className="w-full text-left rounded-2xl bg-gradient-to-br from-purple-600 via-purple-500 to-pink-500 p-5 text-white shadow-lg active:scale-[0.99] transition-transform"
          data-testid="companion-home-combined-card"
        >
          <p className="text-[11px] uppercase tracking-wider font-bold opacity-90">Today is your check-in day</p>
          <p className="text-lg font-bold mt-0.5" style={{ fontFamily: "'Outfit', sans-serif" }}>Weigh-in + injection + measurements</p>
          <p className="text-xs opacity-90 mt-1">One quick flow — tap to start →</p>
        </button>
      )}

      {/* Active Step-a-Thon events — quick tap to log steps (FREE) */}
      {wellness.step_counter_enabled ? (
        <CompanionStepathonCard />
      ) : (
        <ComingSoonOverlay
          message="Coming soon"
          detail={wellness.coming_soon_message}
          testid="coming-soon-stepathon"
        >
          <CompanionStepathonCard />
        </ComingSoonOverlay>
      )}

      {/* Macros rings — paid; unsubs see a compact upgrade tile instead */}
      {hasSub && macros && !macros.needs_setup && <MacroRings macros={macros} />}
      {hasSub && macros && macros.needs_setup && <GoalSetupCard onSaved={load} />}
      {!hasSub && <LockedSectionTile title="Macro rings + calorie tracking" reason={access?.reason} />}

      {/* Calorie ring — paid */}
      {hasSub && (
        <div className="bg-gradient-to-br from-purple-600 to-pink-600 rounded-3xl p-5 text-white" data-testid="companion-home-cal-ring">
          <p className="text-xs opacity-80 uppercase tracking-wider font-bold mb-1">Net calories today</p>
          <p className="text-4xl font-bold" style={{ fontFamily: "'Outfit', sans-serif" }}>{data.net_calories >= 0 ? "+" : ""}{data.net_calories}</p>
          <div className="flex items-center gap-3 mt-3 text-xs opacity-90">
            <span className="inline-flex items-center gap-1"><TrendingUp className="w-3 h-3" /> {data.calories_in} in</span>
            <span className="inline-flex items-center gap-1"><Flame className="w-3 h-3" /> {data.calories_out} out</span>
            {data.weight_today && <span className="inline-flex items-center gap-1 ml-auto"><Activity className="w-3 h-3" /> {data.weight_today} lbs</span>}
          </div>
        </div>
      )}

      {/* Fit-Quiz projection card — paid */}
      {hasSub && fitQuiz && <CompanionFitQuizCard submission={fitQuiz} actualWeights={actualWeights} />}

      {/* Refer-a-friend daily nudge — both get $20 (FREE) */}
      <CompanionReferralBanner />

      {/* Install to home screen (PWA) — auto-hides if already installed / dismissed */}
      <CompanionInstallChip />

      {/* Quick actions — food/exercise are locked for non-subs, weight/injection are free */}
      {hasSub && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <QuickAction emoji="🍳" label="Log breakfast" onClick={() => navigate("/companion/food", { state: { meal: "breakfast" } })} testid="qa-breakfast" />
            <QuickAction emoji="🥗" label="Log lunch" onClick={() => navigate("/companion/food", { state: { meal: "lunch" } })} testid="qa-lunch" />
            <QuickAction emoji="🍽️" label="Log dinner" onClick={() => navigate("/companion/food", { state: { meal: "dinner" } })} testid="qa-dinner" />
            <QuickAction emoji="🍪" label="Log snack" onClick={() => navigate("/companion/food", { state: { meal: "snack" } })} testid="qa-snack" />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <QuickAction emoji="🏃" label="Workout" onClick={() => navigate("/companion/exercise")} testid="qa-exercise" tone="emerald" />
            <QuickAction emoji="⚖️" label="Weight" onClick={() => navigate("/companion/wellness", { state: { mode: "weight" } })} testid="qa-weight" tone="purple" />
            <QuickAction emoji="💉" label="Injection" onClick={() => navigate("/companion/wellness", { state: { mode: "injection" } })} testid="qa-injection" tone="purple" />
          </div>
        </>
      )}
      {!hasSub && (
        <div className="grid grid-cols-3 gap-2">
          <QuickAction emoji="🎁" label="Rewards" onClick={() => navigate("/companion/rewards")} testid="qa-rewards" tone="purple" />
          <QuickAction emoji="⚖️" label="Weight" onClick={() => navigate("/companion/wellness", { state: { mode: "weight" } })} testid="qa-weight" tone="purple" />
          <QuickAction emoji="💉" label="Injection" onClick={() => navigate("/companion/wellness", { state: { mode: "injection" } })} testid="qa-injection" tone="purple" />
        </div>
      )}

      {/* Today's logs */}
      {(data.calorie_logs?.length || 0) + (data.exercise_logs?.length || 0) > 0 && (
        <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-4" data-testid="companion-today-logs">
          <h3 className="text-sm font-bold text-slate-900 dark:text-white mb-2">Today&apos;s log</h3>
          <ul className="space-y-1.5 text-xs">
            {data.calorie_logs.map((l) => (
              <li key={l.id} className="flex items-center justify-between border-b border-slate-100 last:border-0 pb-1.5">
                <span className="truncate flex items-center gap-1.5"><Utensils className="w-3 h-3 text-slate-400" /> {l.food_name} <span className="text-[10px] text-slate-400 capitalize">· {l.meal_type}</span></span>
                <span className="text-slate-900 dark:text-white font-bold">{l.calories} kcal</span>
              </li>
            ))}
            {data.exercise_logs.map((l) => (
              <li key={l.id} className="flex items-center justify-between border-b border-slate-100 last:border-0 pb-1.5">
                <span className="truncate flex items-center gap-1.5"><Dumbbell className="w-3 h-3 text-emerald-500" /> {l.activity} <span className="text-[10px] text-slate-400">· {l.duration_min}m</span></span>
                <span className="text-emerald-700 font-bold">-{l.calories_burned} kcal</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function QuickAction({ emoji, label, onClick, testid, tone = "default" }) {
  const tones = {
    default: "bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 hover:border-purple-300",
    emerald: "bg-emerald-50 border-emerald-200 hover:border-emerald-400 text-emerald-900",
    purple: "bg-purple-50 border-purple-200 hover:border-purple-400 text-purple-900",
  };
  return (
    <button
      onClick={onClick}
      className={`rounded-2xl border p-3 text-left transition-colors ${tones[tone] || tones.default}`}
      data-testid={testid}
    >
      <span className="text-2xl block">{emoji}</span>
      <p className="text-xs font-bold mt-1.5">{label}</p>
    </button>
  );
}

/**
 * Compact tile shown in-place of a paid section for non-subscribers.
 * Explains the perk and links to /medications (subscribe) or the
 * profile access-code redeem page.
 */
function LockedSectionTile({ title, reason }) {
  return (
    <div
      className="rounded-2xl border-2 border-dashed border-purple-200 dark:border-purple-800 bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 p-4"
      data-testid="companion-locked-section-tile"
    >
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 w-9 h-9 rounded-xl bg-white dark:bg-slate-900 border border-purple-200 dark:border-purple-800 inline-flex items-center justify-center">
          <Lock className="w-4 h-4 text-purple-600" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-widest font-bold text-purple-600 dark:text-purple-300">Subscription perk</p>
          <h4 className="text-sm font-bold text-slate-900 dark:text-white mt-0.5">{title}</h4>
          <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5 leading-relaxed">
            Your past logs stay safe. Subscribe to unlock — or redeem a lifetime code.
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            <Link
              to="/medications"
              className="inline-flex items-center gap-1 rounded-full bg-purple-600 hover:bg-purple-700 text-white text-[11px] font-bold px-3 py-1"
              data-testid="companion-locked-tile-subscribe"
            >
              <Sparkles className="w-3 h-3" /> Unlock
            </Link>
            <Link
              to="/profile#companion-access"
              className="inline-flex items-center rounded-full border border-purple-300 dark:border-purple-700 text-purple-700 dark:text-purple-300 text-[11px] font-bold px-3 py-1"
              data-testid="companion-locked-tile-redeem"
            >
              Have a code? Redeem
            </Link>
          </div>
          {reason === "cancelled_or_inactive" && (
            <p className="mt-1.5 text-[10px] text-slate-500 italic">Your subscription ended — resubscribe restores everything instantly.</p>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Full-screen lock UI shown when a non-sub navigates to a paid tab
 * (Food or Move). Reuses the same messaging as LockedSectionTile but
 * takes the whole screen since the entire tab is paid.
 */
function LockedScreen({ feature, reason }) {
  return (
    <div className="p-4" data-testid="companion-locked-screen">
      <ScreenHeader title={feature} subtitle="Subscription required" />
      <AccessGate access={{ has_access: false, reason }} feature={feature} />
    </div>
  );
}

// ===== FOOD =====
function FoodScreen() {
  const access = useCompanionAccess();
  if (access && !access.has_access) {
    return <LockedScreen feature="Food logging" reason={access.reason} />;
  }
  return <FoodScreenInner />;
}

function FoodScreenInner() {
  const location = useLocation();
  const navigate = useNavigate();
  const initialMeal = location.state?.meal || "snack";
  const [meal, setMeal] = useState(initialMeal);
  const [name, setName] = useState("");
  const [cals, setCals] = useState("");
  const [serving, setServing] = useState("");
  const [protein, setProtein] = useState("");
  const [carbs, setCarbs] = useState("");
  const [fat, setFat] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // USDA search
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQ, setSearchQ] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  // Barcode scanner
  const [scanOpen, setScanOpen] = useState(false);

  const runSearch = useCallback(async (q) => {
    if (!q || q.trim().length < 2) { setSearchResults([]); return; }
    setSearching(true);
    try {
      const { data } = await API.get(`/foods/search?q=${encodeURIComponent(q.trim())}&page_size=12`);
      setSearchResults(data.results || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || "Couldn't search foods");
    } finally { setSearching(false); }
  }, []);

  // Debounced search
  useEffect(() => {
    if (!searchOpen) return;
    const t = setTimeout(() => runSearch(searchQ), 350);
    return () => clearTimeout(t);
  }, [searchQ, searchOpen, runSearch]);

  const pickFood = (f) => {
    setName(f.description + (f.brand ? ` (${f.brand})` : ""));
    if (f.calories) setCals(String(Math.round(f.calories)));
    if (f.serving_size && f.serving_size_unit) setServing(`${f.serving_size}${f.serving_size_unit} ${f.household_serving || ""}`.trim());
    if (f.protein_g) setProtein(String(Math.round(f.protein_g * 10) / 10));
    if (f.carbs_g) setCarbs(String(Math.round(f.carbs_g * 10) / 10));
    if (f.fat_g) setFat(String(Math.round(f.fat_g * 10) / 10));
    setSearchOpen(false);
    setSearchQ("");
  };

  const onScanned = async (upc) => {
    setScanOpen(false);
    try {
      const { data } = await API.get(`/foods/upc/${encodeURIComponent(upc)}`);
      pickFood(data.match);
      toast.success(`Found: ${data.match.description?.slice(0, 40)}`);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Barcode not found";
      toast.error(typeof msg === "string" ? msg : "Barcode not found — log it manually");
    }
  };

  const submit = async () => {
    const c = parseInt(cals, 10);
    if (!name.trim() || !c || c < 0) { toast.error("Enter food name + calories"); return; }
    setSubmitting(true);
    try {
      await API.post("/wellness/calories", {
        name: name.trim(), calories: c, meal_type: meal, serving_size: serving.trim() || undefined,
        protein_g: protein ? parseFloat(protein) : undefined,
        carbs_g: carbs ? parseFloat(carbs) : undefined,
        fat_g: fat ? parseFloat(fat) : undefined,
      });
      toast.success(`Logged ${c} kcal · ${meal}`);
      navigate("/companion");
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Failed to log");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="p-4 space-y-4" data-testid="companion-food">
      <ScreenHeader title="Log food" subtitle="Search USDA · scan barcode · or type" />
      <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-4 space-y-4">
        {/* Quick lookup buttons */}
        <div className="grid grid-cols-2 gap-2">
          <Button type="button" variant="outline" onClick={() => setSearchOpen(true)} className="rounded-xl text-xs h-10" data-testid="companion-food-search-btn">
            <Search className="w-3.5 h-3.5 mr-1.5" /> Search foods
          </Button>
          <Button type="button" variant="outline" onClick={() => setScanOpen(true)} className="rounded-xl text-xs h-10 border-emerald-200 text-emerald-700 hover:bg-emerald-50" data-testid="companion-food-scan-btn">
            <ScanLine className="w-3.5 h-3.5 mr-1.5" /> Scan barcode
          </Button>
        </div>

        <div>
          <Label className="text-xs">Meal</Label>
          <div className="grid grid-cols-2 gap-2 mt-1.5">
            {MEAL_PRESETS.map((m) => {
              const Icon = m.icon;
              return (
                <button
                  key={m.id} type="button"
                  onClick={() => setMeal(m.id)}
                  className={`text-sm font-medium rounded-xl py-2.5 px-3 inline-flex items-center justify-center gap-1.5 border transition-colors ${meal === m.id ? `${m.color} border-current` : "bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100"}`}
                  data-testid={`companion-meal-${m.id}`}
                >
                  <Icon className="w-3.5 h-3.5" /> {m.label}
                </button>
              );
            })}
          </div>
        </div>
        <div>
          <Label className="text-xs">What did you eat?</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Greek yogurt with berries" className="mt-1.5 rounded-xl" data-testid="companion-food-name" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Calories</Label>
            <Input value={cals} onChange={(e) => setCals(e.target.value)} type="number" inputMode="numeric" placeholder="e.g. 250" className="mt-1.5 rounded-xl text-lg font-bold text-center" data-testid="companion-food-cals" />
          </div>
          <div>
            <Label className="text-xs">Serving (optional)</Label>
            <Input value={serving} onChange={(e) => setServing(e.target.value)} placeholder="e.g. 1 cup" className="mt-1.5 rounded-xl" />
          </div>
        </div>
        <details className="rounded-lg bg-slate-50 dark:bg-slate-800/40 p-2">
          <summary className="text-[11px] font-medium text-slate-500 cursor-pointer">Macros (optional, helps the daily rings)</summary>
          <div className="grid grid-cols-3 gap-2 mt-2">
            <Input value={protein} onChange={(e) => setProtein(e.target.value)} type="number" placeholder="P (g)" className="rounded-xl text-xs" data-testid="companion-food-protein" />
            <Input value={carbs} onChange={(e) => setCarbs(e.target.value)} type="number" placeholder="C (g)" className="rounded-xl text-xs" data-testid="companion-food-carbs" />
            <Input value={fat} onChange={(e) => setFat(e.target.value)} type="number" placeholder="F (g)" className="rounded-xl text-xs" data-testid="companion-food-fat" />
          </div>
        </details>
        <Button onClick={submit} disabled={submitting || !name.trim() || !cals} className="w-full rounded-xl bg-purple-600 hover:bg-purple-700 text-white text-sm py-5" data-testid="companion-food-save">
          <Plus className="w-4 h-4 mr-2" /> {submitting ? "Logging…" : "Log it"}
        </Button>
      </div>

      {/* USDA search drawer */}
      {searchOpen && (
        <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-end sm:items-center justify-center" data-testid="food-search-drawer">
          <div className="bg-white dark:bg-slate-900 rounded-t-2xl sm:rounded-2xl w-full max-w-md max-h-[80vh] overflow-hidden flex flex-col">
            <div className="flex items-center gap-2 p-3 border-b border-slate-100 dark:border-slate-800">
              <Search className="w-4 h-4 text-slate-400" />
              <input autoFocus value={searchQ} onChange={(e) => setSearchQ(e.target.value)} placeholder="Search USDA database…" className="flex-1 bg-transparent text-sm outline-none" data-testid="food-search-input" />
              <button onClick={() => { setSearchOpen(false); setSearchQ(""); }} className="p-1 hover:bg-slate-100 rounded-full" data-testid="food-search-close"><X className="w-4 h-4 text-slate-500" /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-2">
              {searching && <p className="text-xs text-slate-400 text-center py-6">Searching…</p>}
              {!searching && searchQ.length < 2 && <p className="text-xs text-slate-400 text-center py-6">Type at least 2 letters to search.</p>}
              {!searching && searchQ.length >= 2 && searchResults.length === 0 && <p className="text-xs text-slate-400 text-center py-6">No matches.</p>}
              {searchResults.map((f) => (
                <button key={f.fdc_id} onClick={() => pickFood(f)} className="w-full text-left rounded-lg p-2.5 hover:bg-purple-50 dark:hover:bg-purple-900/20 border-b border-slate-100 dark:border-slate-800 last:border-0" data-testid={`food-search-result-${f.fdc_id}`}>
                  <p className="text-sm font-medium text-slate-900 dark:text-white truncate">{f.description}</p>
                  <p className="text-[10px] text-slate-500 truncate">
                    {f.brand && <span className="font-semibold">{f.brand} · </span>}
                    {f.calories ? `${Math.round(f.calories)} kcal` : ""}
                    {f.protein_g ? ` · P${Math.round(f.protein_g)}` : ""}
                    {f.carbs_g ? ` · C${Math.round(f.carbs_g)}` : ""}
                    {f.fat_g ? ` · F${Math.round(f.fat_g)}` : ""}
                    {f.household_serving ? ` · ${f.household_serving}` : ""}
                  </p>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Barcode scanner */}
      {scanOpen && <BarcodeScanner onResult={onScanned} onClose={() => setScanOpen(false)} />}
    </div>
  );
}

// ===== Barcode scanner =====
function BarcodeScanner({ onResult, onClose }) {
  const containerId = "companion-barcode-reader";
  const scannerRef = (typeof window !== "undefined" && window.__glpfloScannerRef) || { current: null };
  if (typeof window !== "undefined") window.__glpfloScannerRef = scannerRef;
  const [status, setStatus] = useState("starting"); // starting | running | error

  useEffect(() => {
    let cancelled = false;
    let scanner = null;
    (async () => {
      try {
        const mod = await import("html5-qrcode");
        if (cancelled) return;
        const { Html5Qrcode, Html5QrcodeSupportedFormats } = mod;
        // Constrain to the 1D formats printed on consumer packaged goods so
        // the underlying decoder doesn't waste cycles trying to parse a UPC
        // as a QR code. Passing `useBarCodeDetectorIfSupported: true` lets
        // Chrome/Android use the native BarcodeDetector API (10-50× faster
        // than ZXing on-CPU decode).
        scanner = new Html5Qrcode(containerId, {
          verbose: false,
          formatsToSupport: [
            Html5QrcodeSupportedFormats.UPC_A,
            Html5QrcodeSupportedFormats.UPC_E,
            Html5QrcodeSupportedFormats.EAN_13,
            Html5QrcodeSupportedFormats.EAN_8,
            Html5QrcodeSupportedFormats.CODE_128,
            Html5QrcodeSupportedFormats.CODE_39,
            Html5QrcodeSupportedFormats.ITF,
          ],
          useBarCodeDetectorIfSupported: true,
          experimentalFeatures: { useBarCodeDetectorIfSupported: true },
        });
        scannerRef.current = scanner;
        // Try the environment (rear) camera first; some devices reject the
        // exact facing-mode string and we fall back to any available camera.
        const startWith = async (cameraConfig) => scanner.start(
          cameraConfig,
          {
            fps: 10,
            // Responsive qrbox — 80% of the smaller viewport dimension so
            // it fits both portrait phones and rotated devices.
            qrbox: (vw, vh) => {
              const min = Math.min(vw, vh);
              const size = Math.floor(min * 0.8);
              return { width: size, height: Math.floor(size * 0.6) };
            },
            aspectRatio: 1.7778,
            disableFlip: false,
          },
          (decoded) => {
            if (cancelled) return;
            const upc = (decoded || "").trim();
            if (/^\d{8,14}$/.test(upc)) {
              scanner.stop().catch(() => {}).finally(() => onResult(upc));
            }
          },
          () => { /* per-frame parse noise — ignore */ },
        );
        try {
          await startWith({ facingMode: { exact: "environment" } });
        } catch (e1) {
          try {
            await startWith({ facingMode: "environment" });
          } catch (e2) {
            // Last-ditch: enumerate and pick any device
            const devices = await Html5Qrcode.getCameras();
            if (!devices?.length) throw e2;
            await startWith(devices[devices.length - 1].id);
          }
        }
        if (!cancelled) setStatus("running");
      } catch (e) {
        console.error("[BarcodeScanner] failed to start:", e);
        setStatus("error");
        const reason = /NotAllowed|Permission/i.test(String(e?.message || e))
          ? "Camera permission denied. Allow camera access in your browser and try again."
          : /NotFound|Overconstrained/i.test(String(e?.message || e))
            ? "No rear camera found on this device."
            : "Couldn't start camera. You can also search or log manually.";
        toast.error(reason);
        setTimeout(() => onClose(), 400);
      }
    })();
    return () => {
      cancelled = true;
      try {
        if (scanner && scanner.isScanning) {
          scanner.stop().catch(() => {});
        }
      } catch (_) { /* noop */ }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="fixed inset-0 bg-slate-900 z-50 flex flex-col" data-testid="barcode-scanner">
      <div className="flex items-center justify-between p-3">
        <p className="text-white text-sm font-bold">Point camera at barcode</p>
        <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-full" data-testid="barcode-scanner-close"><X className="w-5 h-5 text-white" /></button>
      </div>
      <div id={containerId} className="flex-1 min-h-0 [&_video]:!w-full [&_video]:!h-full [&_video]:object-cover" />
      <p className="text-center text-white/70 text-xs p-4">
        {status === "starting" && "Requesting camera access…"}
        {status === "running" && "Hold the UPC/EAN barcode steady inside the frame."}
        {status === "error" && "Camera unavailable. Closing…"}
      </p>
    </div>
  );
}

// ===== EXERCISE =====
function ExerciseScreen() {
  const access = useCompanionAccess();
  if (access && !access.has_access) {
    return <LockedScreen feature="Exercise / Move logging" reason={access.reason} />;
  }
  return <ExerciseScreenInner />;
}

function ExerciseScreenInner() {
  const navigate = useNavigate();
  const [activity, setActivity] = useState("walking");
  const [duration, setDuration] = useState("30");
  const [intensity, setIntensity] = useState("moderate");
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    const d = parseInt(duration, 10);
    if (!activity || !d || d < 1) { toast.error("Pick activity + minutes"); return; }
    setSubmitting(true);
    try {
      const { data } = await API.post("/wellness/exercise", {
        activity: EXERCISE_PRESETS.find((p) => p.id === activity)?.label || activity,
        duration_min: d,
        intensity,
      });
      toast.success(`Logged · burned ~${data.calories_burned} kcal`);
      navigate("/companion");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to log");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="p-4 space-y-4" data-testid="companion-exercise">
      <ScreenHeader title="Log workout" subtitle="Calories out — auto-estimated" />
      <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-4 space-y-4">
        <div>
          <Label className="text-xs">Activity</Label>
          <div className="grid grid-cols-4 gap-2 mt-1.5">
            {EXERCISE_PRESETS.map((p) => (
              <button
                key={p.id} type="button"
                onClick={() => setActivity(p.id)}
                className={`flex flex-col items-center justify-center rounded-xl py-2 border transition-colors ${activity === p.id ? "bg-emerald-50 border-emerald-400 text-emerald-900" : "bg-slate-50 border-slate-200 hover:bg-slate-100"}`}
                data-testid={`companion-activity-${p.id}`}
              >
                <span className="text-xl">{p.emoji}</span>
                <span className="text-[10px] font-bold mt-0.5">{p.label}</span>
              </button>
            ))}
          </div>
        </div>
        <div>
          <Label className="text-xs">Duration (minutes)</Label>
          <Input value={duration} onChange={(e) => setDuration(e.target.value)} type="number" inputMode="numeric" className="mt-1.5 rounded-xl text-lg font-bold text-center" data-testid="companion-exercise-duration" />
        </div>
        <div>
          <Label className="text-xs">Intensity</Label>
          <div className="grid grid-cols-3 gap-2 mt-1.5">
            {["light", "moderate", "vigorous"].map((i) => (
              <button
                key={i} type="button" onClick={() => setIntensity(i)}
                className={`text-xs font-bold rounded-xl py-2 capitalize border transition-colors ${intensity === i ? "bg-emerald-600 text-white border-emerald-600" : "bg-white border-slate-200 text-slate-700 hover:bg-slate-50"}`}
                data-testid={`companion-intensity-${i}`}
              >
                {i}
              </button>
            ))}
          </div>
        </div>
        <Button onClick={submit} disabled={submitting} className="w-full rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-sm py-5" data-testid="companion-exercise-save">
          <Plus className="w-4 h-4 mr-2" /> {submitting ? "Logging…" : "Log it"}
        </Button>
        <p className="text-[10px] text-slate-500 text-center">Calories burned auto-estimated from MET × your weight × duration. <strong>Approximate</strong>.</p>
      </div>
    </div>
  );
}

// ===== BODY (weight + injection + measurements + side effects) =====
function BodyScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const initialMode = location.state?.mode || "weight";
  const [mode, setMode] = useState(initialMode);
  // weight | injection | measure | side_effect | combined
  const [weight, setWeight] = useState("");
  const [measurements, setMeasurements] = useState({});
  const [measureOpen, setMeasureOpen] = useState(false);
  const [injDrug, setInjDrug] = useState("sema");
  const [injDose, setInjDose] = useState("");
  const [injSite, setInjSite] = useState("");
  const [injNotes, setInjNotes] = useState("");
  const [chips, setChips] = useState([]);
  const [severity, setSeverity] = useState("mild");
  const [seNotes, setSeNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [profile, setProfile] = useState(null);
  const [savingPair, setSavingPair] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  useEffect(() => {
    API.get("/wellness/profile").then(({ data }) => setProfile(data)).catch(() => {});
  }, []);

  const setM = (k, v) => setMeasurements((p) => ({ ...p, [k]: v }));

  const submitWeight = async () => {
    const w = parseFloat(weight);
    if (!w || w < 20 || w > 1500) { toast.error("Enter a valid weight"); return; }
    setSubmitting(true);
    try {
      const { data } = await API.post("/wellness/weight", { weight_lbs: w });
      // Also send any optional measurements taken in the same flow.
      const measPayload = Object.fromEntries(
        Object.entries(measurements)
          .map(([k, v]) => [k, parseFloat(v)])
          .filter(([_k, v]) => Number.isFinite(v) && v > 0)
      );
      if (Object.keys(measPayload).length > 0) {
        await API.post("/wellness/measurements", measPayload).catch(() => {});
      }
      // Daily streak bonus celebration takes precedence.
      const bonus = Number(data?.daily_streak_bonus_points || 0);
      const milestone = Number(data?.daily_streak_bonus_milestone || 0);
      if (bonus > 0) {
        toast.success(`🔥 ${milestone}-day streak! +${bonus} Step-a-Thon points`);
      } else {
        toast.success(data.streak_message || "Weight logged");
      }
      navigate("/companion");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to log");
    } finally { setSubmitting(false); }
  };

  const submitInjection = async () => {
    const dose = parseFloat(injDose);
    if (!dose || dose <= 0 || dose > 30) { toast.error("Enter a valid dose (mg)"); return; }
    setSubmitting(true);
    try {
      const { data } = await API.post("/wellness/injection", {
        drug: injDrug,
        dose_mg: dose,
        site: injSite || undefined,
        notes: injNotes.trim() || undefined,
      });
      toast.success(data.streak_message || "Injection logged");
      navigate("/companion");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to log");
    } finally { setSubmitting(false); }
  };

  const submitCombined = async () => {
    // Pair flow: weight + measurements + injection in one tap. We send the
    // requests sequentially so any single failure surfaces a clean toast
    // without poisoning the rest of the log.
    const w = parseFloat(weight);
    const dose = parseFloat(injDose);
    if (!w || w < 20 || w > 1500) { toast.error("Enter a valid weight"); return; }
    if (!dose || dose <= 0 || dose > 30) { toast.error("Enter a valid dose (mg)"); return; }
    setSubmitting(true);
    try {
      const r1 = await API.post("/wellness/weight", { weight_lbs: w });
      const measPayload = Object.fromEntries(
        Object.entries(measurements)
          .map(([k, v]) => [k, parseFloat(v)])
          .filter(([_k, v]) => Number.isFinite(v) && v > 0)
      );
      if (Object.keys(measPayload).length > 0) {
        await API.post("/wellness/measurements", measPayload).catch(() => {});
      }
      const r2 = await API.post("/wellness/injection", {
        drug: injDrug, dose_mg: dose, site: injSite || undefined, notes: injNotes.trim() || undefined,
      });
      toast.success(`${r1.data.streak_message || "Weight logged"} · ${r2.data.streak_message || "Injection logged"}`);
      navigate("/companion");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to log");
    } finally { setSubmitting(false); }
  };

  const togglePair = async (next) => {
    setSavingPair(true);
    try {
      const { data } = await API.put("/wellness/profile", { pair_weighin_injection: next });
      setProfile(data);
      toast.success(next ? "Weigh-in & injection now paired" : "Tracking weigh-in & injection separately");
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Couldn't update");
    } finally { setSavingPair(false); }
  };

  const toggleChip = (id) => setChips((p) => p.includes(id) ? p.filter((c) => c !== id) : [...p, id]);

  const submitSE = async () => {
    if (chips.length === 0) { toast.error("Pick at least one symptom"); return; }
    setSubmitting(true);
    try {
      const { data } = await API.post("/wellness/side-effect", { chips, severity, notes: seNotes.trim() || undefined });
      if (data.needs_escalation) {
        if (data.chat_available) {
          window.dispatchEvent(new CustomEvent("glpflo:open-chat", { detail: { category: "clinical", prefill: "Just logged a severe side effect — please review my chart." } }));
          navigate("/dashboard");
          return;
        }
        toast.error("Severe symptoms — please call (727) 516-2142. Call 911 for emergencies.", { duration: 12000 });
      } else {
        toast.success("Logged — your prescriber will see this in your chart.");
      }
      navigate("/companion");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to log");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="p-4 space-y-4" data-testid="companion-body">
      <ScreenHeader title="Body" subtitle="Weight · injection · measurements" />

      {/* Pair-toggle setting card — patients can flip pairing on/off any time */}
      {profile && (
        <div className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-3 flex items-center justify-between" data-testid="companion-pair-toggle-card">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-slate-900 dark:text-white">Pair weigh-in &amp; injection</p>
            <p className="text-[11px] text-slate-500 mt-0.5">Log them together each week, or track separately.</p>
          </div>
          <Switch
            checked={!!profile.pair_weighin_injection}
            onCheckedChange={togglePair}
            disabled={savingPair}
            data-testid="companion-pair-toggle"
          />
        </div>
      )}

      <div className="grid grid-cols-4 gap-1.5">
        <ModeBtn active={mode === "weight"} onClick={() => setMode("weight")} testid="weight" emoji="⚖️" label="Weight" />
        <ModeBtn active={mode === "injection"} onClick={() => setMode("injection")} testid="injection" emoji="💉" label="Inject" />
        <ModeBtn active={mode === "combined"} onClick={() => setMode("combined")} testid="combined" emoji="🔁" label="Both" />
        <ModeBtn active={mode === "side_effect"} onClick={() => setMode("side_effect")} testid="symptom" emoji="❤️" label="Symptoms" tone="rose" />
      </div>

      {/* Measurement trend strip — auto-hides when a patient hasn't logged
          ≥2 weeks of any single metric, so it never wastes real estate
          for newcomers. Surfaces the GLP-1 "inches drop before the scale"
          pattern that drives retention. */}
      <MeasurementTrendStrip />

      {mode === "weight" && (
        <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-4 space-y-4">
          <WeightInput value={weight} onChange={setWeight} />
          <MeasurementSection open={measureOpen} setOpen={setMeasureOpen} values={measurements} setValue={setM} />
          <Button onClick={submitWeight} disabled={submitting} className="w-full rounded-xl bg-purple-600 hover:bg-purple-700 text-white text-sm py-5" data-testid="companion-weight-save">
            {submitting ? "Logging…" : "Log weight"}
          </Button>
          <button
            type="button"
            onClick={() => setImportOpen(true)}
            className="w-full text-[12px] font-medium text-purple-600 hover:text-purple-700 flex items-center justify-center gap-1.5 pt-1"
            data-testid="companion-weight-import-open"
          >
            <Upload className="w-3.5 h-3.5" /> Import from Apple Health / CSV
          </button>
          <HealthSyncButton onSynced={() => navigate("/companion")} />
        </div>
      )}

      {mode === "injection" && (
        <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-4 space-y-4">
          <InjectionInputs
            drug={injDrug} setDrug={setInjDrug}
            dose={injDose} setDose={setInjDose}
            site={injSite} setSite={setInjSite}
            notes={injNotes} setNotes={setInjNotes}
          />
          <Button onClick={submitInjection} disabled={submitting} className="w-full rounded-xl bg-purple-600 hover:bg-purple-700 text-white text-sm py-5" data-testid="companion-injection-save">
            {submitting ? "Logging…" : "Log injection"}
          </Button>
        </div>
      )}

      {mode === "combined" && (
        <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-4 space-y-4" data-testid="companion-combined-card">
          <WeightInput value={weight} onChange={setWeight} />
          <MeasurementSection open={measureOpen} setOpen={setMeasureOpen} values={measurements} setValue={setM} />
          <div className="border-t border-slate-200 dark:border-slate-700 pt-4">
            <p className="text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-2">Today&apos;s injection</p>
            <InjectionInputs
              drug={injDrug} setDrug={setInjDrug}
              dose={injDose} setDose={setInjDose}
              site={injSite} setSite={setInjSite}
              notes={injNotes} setNotes={setInjNotes}
            />
          </div>
          <Button onClick={submitCombined} disabled={submitting} className="w-full rounded-xl bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white text-sm py-5" data-testid="companion-combined-save">
            {submitting ? "Logging…" : "Log everything"}
          </Button>
        </div>
      )}

      {mode === "side_effect" && (
        <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-4 space-y-4">
          <div>
            <Label className="text-xs">What are you feeling? (tap all)</Label>
            <div className="grid grid-cols-2 gap-1.5 mt-1.5">
              {SIDE_EFFECT_CHIPS.map((c) => (
                <button key={c.id} type="button" onClick={() => toggleChip(c.id)}
                  className={`text-xs rounded-full py-1.5 px-2 border transition-colors ${chips.includes(c.id) ? "bg-rose-600 text-white border-rose-600" : "bg-white border-slate-200 text-slate-700"}`}
                  data-testid={`companion-chip-${c.id}`}>{c.label}</button>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-xs">Severity</Label>
            <div className="grid grid-cols-3 gap-1.5 mt-1.5">
              {[
                { id: "mild", color: "bg-emerald-600 border-emerald-600" },
                { id: "moderate", color: "bg-amber-600 border-amber-600" },
                { id: "severe", color: "bg-rose-600 border-rose-600" },
              ].map((s) => (
                <button key={s.id} type="button" onClick={() => setSeverity(s.id)}
                  className={`text-xs font-bold rounded-xl py-2 capitalize border transition-colors ${severity === s.id ? `${s.color} text-white` : "bg-white border-slate-200 text-slate-700"}`}
                  data-testid={`companion-severity-${s.id}`}>{s.id}</button>
              ))}
            </div>
          </div>
          <Input value={seNotes} onChange={(e) => setSeNotes(e.target.value)} placeholder="Notes (optional)" maxLength={500} className="rounded-xl" data-testid="companion-se-notes" />
          <Button onClick={submitSE} disabled={submitting || chips.length === 0} className="w-full rounded-xl bg-rose-600 hover:bg-rose-700 text-white text-sm py-5" data-testid="companion-se-save">
            {submitting ? "Logging…" : "Log symptoms"}
          </Button>
          {severity === "severe" && (
            <p className="text-[10px] text-rose-700 bg-rose-50 border border-rose-200 rounded p-2">⚠️ <strong>For emergencies, call 911.</strong> If our chat team is online we&apos;ll connect you instantly. Otherwise we&apos;ll show you our clinical care team phone number.</p>
          )}
        </div>
      )}

      {/* Shot reminders — FREE tier feature, always visible on Body tab. */}
      <ShotRemindersCard />

      {importOpen && (
        <WeightImportModal
          onClose={() => setImportOpen(false)}
          onImported={() => { setImportOpen(false); navigate("/companion"); }}
        />
      )}
    </div>
  );
}

// --- BodyScreen helper components ---
function toIsoDate(s) {
  if (!s) return null;
  const str = String(s).trim();
  const m = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[1]}-${m[2]}-${m[3]}`;
  const t = Date.parse(str);
  if (Number.isNaN(t)) return null;
  const d = new Date(t);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function parseWeightCsv(text, unit) {
  const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  if (!lines.length) return [];
  const delim = lines[0].includes("\t") ? "\t" : lines[0].includes(";") ? ";" : ",";
  const rows = lines.map((l) => l.split(delim).map((c) => c.trim().replace(/^"|"$/g, "")));
  const header = rows[0].map((h) => h.toLowerCase());
  const looksHeader =
    header.some((h) => /(date|time|start|weight|mass|value|lb|kg)/.test(h)) &&
    header.some((h) => Number.isNaN(parseFloat(h)));
  let dateIdx = -1, weightIdx = -1, start = 0;
  let colUnit = unit;
  if (looksHeader) {
    start = 1;
    dateIdx = header.findIndex((h) => /(date|time|start)/.test(h));
    weightIdx = header.findIndex((h) => /(weight|mass|value|lb|kg)/.test(h));
    const wh = header[weightIdx] || "";
    if (/kg/.test(wh)) colUnit = "kg";
    else if (/lb/.test(wh)) colUnit = "lbs";
  }
  const seen = new Set();
  const out = [];
  for (let i = start; i < rows.length; i++) {
    const r = rows[i];
    let dRaw, wRaw;
    if (dateIdx >= 0 && weightIdx >= 0) { dRaw = r[dateIdx]; wRaw = r[weightIdx]; }
    else {
      dRaw = r.find((c) => !Number.isNaN(Date.parse(c)));
      wRaw = r.find((c) => c !== dRaw && Number.isFinite(parseFloat(c)));
    }
    const iso = toIsoDate(dRaw);
    let w = parseFloat(wRaw);
    if (!iso || !Number.isFinite(w)) continue;
    if (colUnit === "kg") w = w * 2.20462;
    w = Math.round(w * 10) / 10;
    if (w <= 20 || w >= 1500 || seen.has(iso)) continue;
    seen.add(iso);
    out.push({ date: iso, weight_lbs: w });
  }
  return out;
}

function WeightImportModal({ onClose, onImported }) {
  const [method, setMethod] = useState("csv"); // csv | manual
  const [unit, setUnit] = useState("lbs");
  const [parsed, setParsed] = useState([]);
  const [fileName, setFileName] = useState("");
  const [rows, setRows] = useState([{ date: "", weight: "" }]);
  const [busy, setBusy] = useState(false);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFileName(f.name);
    try {
      const text = await f.text();
      const entries = parseWeightCsv(text, unit);
      setParsed(entries);
      if (!entries.length) toast.error("Couldn't find any weight rows in that file.");
      else toast.success(`Found ${entries.length} weigh-in${entries.length === 1 ? "" : "s"}.`);
    } catch {
      toast.error("Could not read that file.");
    }
  };

  const manualEntries = () =>
    rows
      .map((r) => {
        const iso = toIsoDate(r.date);
        let w = parseFloat(r.weight);
        if (!iso || !Number.isFinite(w)) return null;
        if (unit === "kg") w = w * 2.20462;
        w = Math.round(w * 10) / 10;
        if (w <= 20 || w >= 1500) return null;
        return { date: iso, weight_lbs: w };
      })
      .filter(Boolean);

  const entries = method === "csv" ? parsed : manualEntries();

  const doImport = async () => {
    if (!entries.length) { toast.error("Add at least one valid weigh-in."); return; }
    setBusy(true);
    try {
      const { data } = await API.post("/wellness/weight/import", {
        entries,
        source: method === "csv" ? "apple_health_csv" : "manual",
      });
      toast.success(`Imported ${data.imported} weigh-in${data.imported === 1 ? "" : "s"}${data.skipped ? ` · ${data.skipped} skipped` : ""}.`);
      onImported();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center bg-slate-900/50 backdrop-blur-sm p-0 sm:p-4" data-testid="weight-import-modal">
      <div className="w-full sm:max-w-md bg-white dark:bg-slate-900 rounded-t-3xl sm:rounded-3xl shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 dark:border-slate-800 sticky top-0 bg-white dark:bg-slate-900">
          <h3 className="font-semibold text-slate-900 dark:text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>Import weight history</h3>
          <button onClick={onClose} disabled={busy} className="text-slate-400 hover:text-slate-600" data-testid="weight-import-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-2">
            <button type="button" onClick={() => setMethod("csv")} className={`rounded-xl py-2 text-xs font-bold border ${method === "csv" ? "bg-purple-600 text-white border-purple-600" : "bg-white dark:bg-slate-800 border-slate-200 text-slate-700 dark:text-slate-200"}`} data-testid="weight-import-tab-csv">Upload CSV</button>
            <button type="button" onClick={() => setMethod("manual")} className={`rounded-xl py-2 text-xs font-bold border ${method === "manual" ? "bg-purple-600 text-white border-purple-600" : "bg-white dark:bg-slate-800 border-slate-200 text-slate-700 dark:text-slate-200"}`} data-testid="weight-import-tab-manual">Type entries</button>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-slate-500">Units in your data</span>
            <div className="flex rounded-lg overflow-hidden border border-slate-200 dark:border-slate-700">
              {["lbs", "kg"].map((u) => (
                <button key={u} type="button" onClick={() => setUnit(u)} className={`px-3 py-1 text-xs font-bold ${unit === u ? "bg-slate-900 text-white dark:bg-white dark:text-slate-900" : "bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300"}`} data-testid={`weight-import-unit-${u}`}>{u}</button>
              ))}
            </div>
          </div>

          {method === "csv" && (
            <div className="space-y-2">
              <label className="block rounded-2xl border-2 border-dashed border-purple-200 dark:border-purple-900 bg-purple-50/50 dark:bg-purple-950/20 p-6 text-center cursor-pointer hover:bg-purple-50" data-testid="weight-import-dropzone">
                <input type="file" accept=".csv,text/csv,text/plain" className="hidden" onChange={onFile} data-testid="weight-import-file" />
                <Upload className="w-6 h-6 text-purple-500 mx-auto mb-2" />
                <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">{fileName || "Choose a CSV file"}</p>
                <p className="text-[11px] text-slate-500 mt-1">Export weight from Apple Health, Fitbit, or any app that gives a date + weight CSV.</p>
              </label>
              {parsed.length > 0 && (
                <div className="flex items-center gap-2 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2" data-testid="weight-import-preview">
                  <FileText className="w-4 h-4" /> {parsed.length} weigh-ins ready ({parsed[parsed.length - 1].date} → {parsed[0].date})
                </div>
              )}
            </div>
          )}

          {method === "manual" && (
            <div className="space-y-2">
              {rows.map((r, i) => (
                <div key={i} className="flex gap-2" data-testid={`weight-import-row-${i}`}>
                  <Input type="date" value={r.date} onChange={(e) => setRows((p) => p.map((x, idx) => idx === i ? { ...x, date: e.target.value } : x))} className="rounded-xl text-sm" data-testid={`weight-import-date-${i}`} />
                  <Input type="number" inputMode="decimal" placeholder={`Weight (${unit})`} value={r.weight} onChange={(e) => setRows((p) => p.map((x, idx) => idx === i ? { ...x, weight: e.target.value } : x))} className="rounded-xl text-sm w-28" data-testid={`weight-import-weight-${i}`} />
                  {rows.length > 1 && (
                    <button type="button" onClick={() => setRows((p) => p.filter((_, idx) => idx !== i))} className="text-slate-400 hover:text-rose-500 px-1" data-testid={`weight-import-remove-${i}`}><X className="w-4 h-4" /></button>
                  )}
                </div>
              ))}
              <button type="button" onClick={() => setRows((p) => [...p, { date: "", weight: "" }])} className="text-xs font-semibold text-purple-600 hover:text-purple-700 flex items-center gap-1" data-testid="weight-import-add-row">
                <Plus className="w-3.5 h-3.5" /> Add another day
              </button>
            </div>
          )}

          <p className="text-[11px] text-slate-400">Backdated entries fill in your trend chart. They don&apos;t count toward the 5-week weigh-in streak or reward coupons. Days you&apos;ve already logged are skipped.</p>

          <Button onClick={doImport} disabled={busy || !entries.length} className="w-full rounded-xl bg-purple-600 hover:bg-purple-700 text-white text-sm py-5" data-testid="weight-import-submit">
            {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Importing…</> : `Import ${entries.length || ""} weigh-in${entries.length === 1 ? "" : "s"}`}
          </Button>
        </div>
      </div>
    </div>
  );
}

function ModeBtn({ active, onClick, testid, emoji, label, tone }) {
  const isRose = tone === "rose";
  const activeCls = isRose ? "bg-rose-600 text-white" : "bg-purple-600 text-white";
  return (
    <button
      onClick={onClick}
      className={`rounded-xl py-2.5 text-[11px] font-bold flex flex-col items-center gap-0.5 ${active ? activeCls : "bg-white border border-slate-200 text-slate-700"}`}
      data-testid={`companion-body-mode-${testid}`}
    >
      <span className="text-base leading-none">{emoji}</span>
      {label}
    </button>
  );
}

function WeightInput({ value, onChange }) {
  return (
    <div>
      <Label className="text-xs">Weight (lbs)</Label>
      <Input value={value} onChange={(e) => onChange(e.target.value)} type="number" step="0.1" inputMode="decimal" className="mt-1.5 rounded-xl text-2xl font-bold text-center py-6" placeholder="0.0" data-testid="companion-weight-input" autoFocus />
    </div>
  );
}

const MEASUREMENT_FIELDS = [
  { key: "waist_in", label: "Waist" },
  { key: "hip_in", label: "Hips" },
  { key: "chest_in", label: "Chest" },
  { key: "left_arm_in", label: "Left arm" },
  { key: "right_arm_in", label: "Right arm" },
  { key: "left_thigh_in", label: "Left thigh" },
  { key: "right_thigh_in", label: "Right thigh" },
];

function MeasurementSection({ open, setOpen, values, setValue }) {
  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-700">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-3 py-2.5 text-left"
        data-testid="companion-measurements-toggle"
      >
        <span className="text-xs font-semibold text-slate-700 dark:text-slate-200 flex items-center gap-1.5">
          <Ruler className="w-3.5 h-3.5 text-purple-600" /> Measurements (optional)
        </span>
        {open ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
      </button>
      {open && (
        <div className="px-3 pb-3 grid grid-cols-2 gap-2" data-testid="companion-measurements-section">
          {MEASUREMENT_FIELDS.map((f) => (
            <div key={f.key}>
              <Label className="text-[10px] text-slate-500">{f.label} (in)</Label>
              <Input
                value={values[f.key] || ""}
                onChange={(e) => setValue(f.key, e.target.value)}
                type="number"
                step="0.1"
                inputMode="decimal"
                placeholder="—"
                className="mt-0.5 rounded-lg text-sm h-9"
                data-testid={`companion-measure-${f.key}`}
              />
            </div>
          ))}
          <p className="col-span-2 text-[10px] text-slate-400 leading-snug">Skip any you don&apos;t want to track. Same units (inches) each week keeps your trend chart clean.</p>
        </div>
      )}
    </div>
  );
}

function InjectionInputs({ drug, setDrug, dose, setDose, site, setSite, notes, setNotes }) {
  return (
    <>
      <div>
        <Label className="text-xs">Medication</Label>
        <div className="grid grid-cols-2 gap-1.5 mt-1.5">
          {[
            { id: "sema", label: "Semaglutide" },
            { id: "tirz", label: "Tirzepatide" },
          ].map((d) => (
            <button key={d.id} type="button" onClick={() => setDrug(d.id)}
              className={`text-xs font-bold rounded-xl py-2.5 border transition-colors ${drug === d.id ? "bg-purple-600 text-white border-purple-600" : "bg-white border-slate-200 text-slate-700"}`}
              data-testid={`companion-injection-drug-${d.id}`}>{d.label}</button>
          ))}
        </div>
      </div>
      <div>
        <Label className="text-xs">Dose (mg)</Label>
        <Input value={dose} onChange={(e) => setDose(e.target.value)} type="number" step="0.05" inputMode="decimal" className="mt-1.5 rounded-xl text-xl font-bold text-center py-4" placeholder="e.g. 5.0" data-testid="companion-injection-dose" />
      </div>
      <div>
        <Label className="text-xs flex items-center gap-1"><Syringe className="w-3 h-3 text-purple-600" /> Injection site (optional)</Label>
        <div className="grid grid-cols-3 gap-1.5 mt-1.5">
          {[
            { id: "belly", label: "Belly" },
            { id: "left_arm", label: "L arm" },
            { id: "right_arm", label: "R arm" },
            { id: "left_thigh", label: "L thigh" },
            { id: "right_thigh", label: "R thigh" },
            { id: "", label: "Skip" },
          ].map((s) => (
            <button key={s.id || "skip"} type="button" onClick={() => setSite(s.id)}
              className={`text-[11px] rounded-lg py-1.5 border transition-colors ${site === s.id ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 text-slate-600"}`}
              data-testid={`companion-injection-site-${s.id || "skip"}`}>{s.label}</button>
          ))}
        </div>
      </div>
      <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes (optional)" maxLength={500} className="rounded-xl" data-testid="companion-injection-notes" />
    </>
  );
}

// ===== Fit Quiz projection card =====
function CompanionFitQuizCard({ submission, actualWeights }) {
  // submission has: weight_lb (start), recommended_plan ("sema-mid" etc),
  // created_at. Map plan → drug + dose for <FitQuizProjection />.
  const plan = (submission?.recommended_plan || "").split("-");
  const drug = plan[0];
  const dose = plan[1];
  const startWeight = parseFloat(submission?.weight_lb || 0);
  if (!drug || !dose || !startWeight) return null;
  // Compute weeks-since-quiz so we can map each actual weight log onto
  // the projected curve's X-axis (week number).
  const quizDate = submission?.created_at ? new Date(submission.created_at) : null;
  const actualPoints = (Array.isArray(actualWeights) ? actualWeights : [])
    .filter((p) => p?.logged_at && p?.weight_lbs)
    .map((p) => {
      const wks = quizDate ? Math.max(0, Math.round((new Date(p.logged_at) - quizDate) / (7 * 86400000))) : null;
      return wks !== null && wks <= 13 ? { week: wks, weight: p.weight_lbs } : null;
    })
    .filter(Boolean)
    .sort((a, b) => a.week - b.week);
  return (
    <div className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-3" data-testid="companion-fitquiz-card">
      <FitQuizProjection startWeight={startWeight} drug={drug} dose={dose} actualPoints={actualPoints} />
      {actualPoints.length > 0 && (
        <p className="text-[10px] text-emerald-700 mt-1.5 px-1">
          {actualPoints.length} actual weigh-in{actualPoints.length === 1 ? "" : "s"} plotted on top of your projection.
        </p>
      )}
    </div>
  );
}


// ===== Macros ring widget =====
function MacroRing({ label, current, target, color, testid }) {
  const pct = target > 0 ? Math.min(100, (current / target) * 100) : 0;
  const r = 26;
  const c = 2 * Math.PI * r;
  const offset = c * (1 - pct / 100);
  return (
    <div className="flex flex-col items-center" data-testid={testid}>
      <div className="relative w-16 h-16">
        <svg className="w-16 h-16 -rotate-90" viewBox="0 0 64 64">
          <circle cx="32" cy="32" r={r} stroke="#e2e8f0" strokeWidth="6" fill="none" />
          <circle cx="32" cy="32" r={r} stroke={color} strokeWidth="6" fill="none"
            strokeDasharray={c} strokeDashoffset={offset} strokeLinecap="round"
            style={{ transition: "stroke-dashoffset 600ms" }} />
        </svg>
        <span className="absolute inset-0 flex items-center justify-center text-[11px] font-bold text-slate-900 dark:text-white">
          {Math.round(pct)}%
        </span>
      </div>
      <p className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mt-1">{label}</p>
      <p className="text-[11px] font-semibold text-slate-700 dark:text-slate-300">{Math.round(current)}/{target}g</p>
    </div>
  );
}

function MacroRings({ macros }) {
  const t = macros.targets;
  const c = macros.consumed;
  return (
    <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-4" data-testid="companion-macro-rings">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <h3 className="text-sm font-bold text-slate-900 dark:text-white">Today&apos;s macros</h3>
          <p className="text-[10px] text-slate-500">{c.calories} / {t.calories_target} kcal · 1g/lb protein for lean-mass preservation</p>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2">
        <MacroRing label="Protein" current={c.protein_g} target={t.protein_g_target} color="#7c3aed" testid="macro-ring-protein" />
        <MacroRing label="Carbs" current={c.carbs_g} target={t.carbs_g_target} color="#f59e0b" testid="macro-ring-carbs" />
        <MacroRing label="Fat" current={c.fat_g} target={t.fat_g_target} color="#ec4899" testid="macro-ring-fat" />
      </div>
    </div>
  );
}

const ACTIVITY_OPTIONS = [
  { id: "sedentary", label: "Sedentary", desc: "Desk job, little exercise" },
  { id: "light", label: "Light", desc: "Light exercise 1-3 days/wk" },
  { id: "moderate", label: "Moderate", desc: "Exercise 3-5 days/wk" },
  { id: "very_active", label: "Very active", desc: "Hard exercise 6-7 days/wk" },
];

function GoalSetupCard({ onSaved }) {
  const [tw, setTw] = useState("");
  const [activity, setActivity] = useState("light");
  const [submitting, setSubmitting] = useState(false);
  const save = async () => {
    const w = parseFloat(tw);
    if (!w || w < 50 || w > 800) { toast.error("Enter a realistic target weight"); return; }
    setSubmitting(true);
    try {
      await API.put("/wellness/goals", { target_weight_lbs: w, activity_level: activity });
      toast.success("Goals set! Macro rings unlocked.");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Failed to save");
    } finally { setSubmitting(false); }
  };
  return (
    <div className="bg-gradient-to-br from-emerald-50 to-purple-50 dark:from-emerald-900/20 dark:to-purple-900/20 border-2 border-dashed border-emerald-300 rounded-2xl p-4" data-testid="companion-goal-setup">
      <h3 className="text-sm font-bold text-slate-900 dark:text-white mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>
        🎯 Set your daily goals (60 sec)
      </h3>
      <p className="text-[11px] text-slate-600 dark:text-slate-300 mb-3">
        We&apos;ll auto-calculate your protein, carbs, and fat targets — protein at 1g per lb of target weight to protect lean mass during GLP-1 weight loss.
      </p>
      <div className="space-y-3">
        <div>
          <Label className="text-xs">Target weight (lbs)</Label>
          <Input type="number" step="0.1" inputMode="decimal" value={tw} onChange={(e) => setTw(e.target.value)} placeholder="e.g. 165" className="mt-1 rounded-xl" data-testid="goal-target-weight" autoFocus />
        </div>
        <div>
          <Label className="text-xs">How active are you?</Label>
          <div className="grid grid-cols-2 gap-1.5 mt-1.5">
            {ACTIVITY_OPTIONS.map((a) => (
              <button key={a.id} type="button" onClick={() => setActivity(a.id)}
                className={`text-left text-xs rounded-lg p-2 border transition-colors ${activity === a.id ? "bg-emerald-600 text-white border-emerald-600" : "bg-white border-slate-200 text-slate-700 hover:border-emerald-200"}`}
                data-testid={`goal-activity-${a.id}`}>
                <p className="font-bold">{a.label}</p>
                <p className="text-[10px] opacity-80">{a.desc}</p>
              </button>
            ))}
          </div>
        </div>
        <Button onClick={save} disabled={submitting || !tw} className="w-full rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-sm" data-testid="goal-save">
          {submitting ? "Saving…" : "Lock it in"}
        </Button>
      </div>
    </div>
  );
}

// ===== Helpers =====
function ScreenLoader() {
  return <div className="p-4 space-y-3 animate-pulse"><div className="bg-slate-200 h-32 rounded-2xl" /><div className="bg-slate-200 h-24 rounded-2xl" /></div>;
}
function ScreenHeader({ title, subtitle }) {
  const navigate = useNavigate();
  return (
    <header className="flex items-center gap-2">
      <button onClick={() => navigate("/companion")} className="p-2 -ml-2 rounded-full hover:bg-slate-100" data-testid="companion-back">
        <ArrowLeft className="w-4 h-4 text-slate-500" />
      </button>
      <div>
        <h1 className="text-xl font-bold text-slate-900 dark:text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>{title}</h1>
        <p className="text-xs text-slate-500">{subtitle}</p>
      </div>
    </header>
  );
}
